import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '.'
import { StudentRole, User, UserRole } from '../models/User'
import { ApiResult, LoadingStatus } from '../models/Enums'
import axios from 'axios'
import { PURGE } from 'redux-persist'
import { axiosPost } from '../components/Common/Axios'

const name = 'user'
const MAX_RECENT_JOB = 10

export type UserState = {
  me: User
  accessToken: string
  refreshToken: string
  loginStatus: number
  signupStatus: number
  apiResult: number
  menuIndex: number
  selectGroupId: number
}

const initialState: UserState = {
  me: {
    id: 0,
    email: '',
    userid: '',
    username: '',
    profileImage: '',
    userRole: UserRole.GUEST,
    groupId: 0,
    groupName: '',
    setting: { isInventoryShowGrid: true },
    recentJobs: '[]',
    isUpdatePassword: false,
    checked: false,
    recommendContent: '',
    studentRole: StudentRole.NORMAL,
  },
  accessToken: '',
  refreshToken: '',
  loginStatus: LoadingStatus.NONE,
  signupStatus: LoadingStatus.NONE,
  apiResult: ApiResult.NONE,
  menuIndex: 0,
  selectGroupId: 0,
}

///--------------------------------------------
/// Login
///--------------------------------------------
export interface LoginFormValues {
  userid: string
  password: string
}

export const asyncLogin = createAsyncThunk(
  `${name}/login`,
  async (formValues: LoginFormValues, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVER}/api/auth/login`,
        formValues
      )

      // 로그인 오류
      if (response.data.result == ApiResult.NOT_EXIST) {
        return thunkAPI.rejectWithValue(ApiResult.NOT_EXIST)
      }

      return response.data as UserState
    } catch (error) {
      console.log(`asyncLogin Error ${error}`)
      return thunkAPI.rejectWithValue(ApiResult.UNKNOWN_ERROR)
    }
  }
)

///--------------------------------------------
/// SignUp
///--------------------------------------------
export interface SignUpFormValues {
  userid: string
  username: string
  email: string
  password: string
  password2: string
  group: string
}

export const asyncSignup = createAsyncThunk(
  `${name}/signup`,
  async (formValues: SignUpFormValues, thunkAPI) => {
    try {
      const payload = {
        userid: formValues.userid,
        username: formValues.username,
        email: formValues.email,
        password: formValues.password,
        group: formValues.group,
      }

      const data = await axiosPost('/api/auth/signup', payload)

      // 이미 존재하는 ID
      if (data.result === ApiResult.ALREADY_EXIST) {
        alert('이미 존재하는 아이디 입니다')
        return thunkAPI.rejectWithValue(ApiResult.ALREADY_EXIST)
      }

      return data as UserState
    } catch (error) {
      console.log(`asyncSignup Error ${error}`)
      return thunkAPI.rejectWithValue(ApiResult.UNKNOWN_ERROR)
    }
  }
)

///--------------------------------------------
/// Slice
///--------------------------------------------
export const userSlice = createSlice({
  name,
  initialState,
  reducers: {
    // setUser: (state, action: PayloadAction<User>) => {
    //   state.user = action.payload
    // },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload
    },
    resetApiResult: (state, action: PayloadAction) => {
      state.apiResult = ApiResult.NONE
    },
    setMenuIndex: (state, action: PayloadAction<number>) => {
      state.menuIndex = action.payload
    },
    setIsInventoryGrid: (state, action: PayloadAction<boolean>) => {
      state.me.setting.isInventoryShowGrid = action.payload
    },
    setStudentRole: (state, action: PayloadAction<number>) => {
      state.me.studentRole = action.payload
    },
    appendRecentJob: (state, action: PayloadAction<number>) => {
      console.log('appendRecentJob', state.me.recentJobs)
      if (!state.me.recentJobs) {
        state.me.recentJobs = '[]'
      }
      let recentJobs = JSON.parse(state.me.recentJobs) as number[]

      if (recentJobs.length > 0 && recentJobs.includes(action.payload)) {
        recentJobs = recentJobs.filter((job) => job !== action.payload)
      }
      recentJobs.push(action.payload)

      if (recentJobs.length > MAX_RECENT_JOB) {
        recentJobs.shift()
      }

      const recentJobStr = JSON.stringify(recentJobs)

      state.me.recentJobs = recentJobStr

      axiosPost('/api/user/recent_job', {
        recentJob: recentJobStr,
      })
    },
    updateUserid: (state, action: PayloadAction<string>) => {
      state.me.userid = action.payload
    },
    updateUsername: (state, action: PayloadAction<string>) => {
      state.me.username = action.payload
    },
    setSelectGroupId: (state, action: PayloadAction<number>) => {
      state.selectGroupId = action.payload
      state.me.groupId = action.payload
    },
  },
  extraReducers: (builder) => {
    // login
    builder.addCase(asyncLogin.pending, (state, action) => {
      state.loginStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncLogin.fulfilled,
      (state, action: PayloadAction<UserState>) => {
        state.me = action.payload.me
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.loginStatus = LoadingStatus.FULFILLED

        sessionStorage.setItem('accessToken', action.payload.accessToken)
        sessionStorage.setItem('refreshToken', action.payload.refreshToken)
      }
    )
    builder.addCase(asyncLogin.rejected, (state, action) => {
      state.apiResult = action.payload as number
      state.loginStatus = LoadingStatus.REJECTED
    })

    // signup
    builder.addCase(asyncSignup.pending, (state, action) => {
      state.signupStatus = LoadingStatus.PENDING
    })
    builder.addCase(
      asyncSignup.fulfilled,
      (state, action: PayloadAction<UserState>) => {
        state.me = action.payload.me
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.signupStatus = LoadingStatus.FULFILLED

        sessionStorage.setItem('accessToken', action.payload.accessToken)
        sessionStorage.setItem('refreshToken', action.payload.refreshToken)
      }
    )
    builder.addCase(asyncSignup.rejected, (state, action) => {
      console.log('rejected', action.payload)
      state.apiResult = action.payload as number
      state.signupStatus = LoadingStatus.REJECTED
    })
    // logout
    builder.addCase(PURGE, () => initialState)
  },
})

export const {
  setAccessToken,
  resetApiResult,
  setMenuIndex,
  setStudentRole,
  setIsInventoryGrid,
  appendRecentJob,
  updateUserid,
  updateUsername,
  setSelectGroupId,
} = userSlice.actions
export const user = (state: RootState) => state.user.me
export default userSlice.reducer
